import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { WalletIcon, StarIcon, GiftIcon, ArrowRightIcon, CheckCircleIcon, SparklesIcon, CrownIcon, ClockIcon, UsersIcon, TrendingUpIcon, ShieldCheckIcon, FlameIcon, ZapIcon, CheckIcon } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { useAuth } from '../../lib/AuthContext';
import { SavingsCalculator } from '../../components/customer/SavingsCalculator';
import { MemberCard3D } from '../../components/customer/MemberCard3D';
import { SpinWheelPopup } from '../../components/customer/SpinWheelPopup';
// Countdown timer hook
function useCountdown() {
  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0
  });
  useEffect(() => {
    function getEndOfMonth() {
      const now = new Date();
      return new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999);
    }
    function calc() {
      const now = new Date();
      const end = getEndOfMonth();
      const diff = Math.max(0, end.getTime() - now.getTime());
      return {
        days: Math.floor(diff / 86400000),
        hours: Math.floor(diff % 86400000 / 3600000),
        minutes: Math.floor(diff % 3600000 / 60000),
        seconds: Math.floor(diff % 60000 / 1000)
      };
    }
    setTimeLeft(calc());
    const interval = setInterval(() => setTimeLeft(calc()), 1000);
    return () => clearInterval(interval);
  }, []);
  return timeLeft;
}
// Live activity feed data
const ACTIVITY_FEED = [{
  name: 'Nguyễn V. An',
  action: 'vừa đăng ký thành viên',
  time: '2 phút trước',
  icon: '🎉'
}, {
  name: 'Trần T. Bích',
  action: 'vừa nạp 50.000.000đ',
  time: '5 phút trước',
  icon: '💰'
}, {
  name: 'Lê H. Cường',
  action: 'nâng hạng lên Bạc',
  time: '8 phút trước',
  icon: '🥈'
}, {
  name: 'Phạm T. Dung',
  action: 'vừa đổi 1.000 điểm',
  time: '12 phút trước',
  icon: '🎁'
}, {
  name: 'Võ M. Đức',
  action: 'vừa nạp 100.000.000đ',
  time: '15 phút trước',
  icon: '💰'
}, {
  name: 'Huỳnh T. Hoa',
  action: 'nâng hạng lên Vàng',
  time: '18 phút trước',
  icon: '🥇'
}, {
  name: 'Đặng Q. Khánh',
  action: 'vừa đăng ký thành viên',
  time: '22 phút trước',
  icon: '🎉'
}, {
  name: 'Bùi T. Minh',
  action: 'vừa nạp 30.000.000đ',
  time: '25 phút trước',
  icon: '💰'
}];
function CountdownUnit({
  value,
  label
}: {
  value: number;
  label: string;
}) {
  return <div className="flex flex-col items-center" data-id="element-1151">
      <div className="bg-[#0D0D0D] border border-[#2A2A2A] rounded-lg w-12 h-12 md:w-14 md:h-14 flex items-center justify-center" data-id="element-1152">
        <AnimatePresence mode="popLayout" data-id="element-1153">
          <motion.span key={value} initial={{
          y: -10,
          opacity: 0
        }} animate={{
          y: 0,
          opacity: 1
        }} exit={{
          y: 10,
          opacity: 0
        }} transition={{
          duration: 0.2
        }} className="text-lg md:text-xl font-bold text-white tabular-nums" data-id="element-1154">
            {value.toString().padStart(2, '0')}
          </motion.span>
        </AnimatePresence>
      </div>
      <span className="text-[9px] md:text-[10px] text-[#666666] mt-1 uppercase tracking-wider" data-id="element-1155">
        {label}
      </span>
    </div>;
}
function LiveActivityFeed() {
  const [currentIndex, setCurrentIndex] = useState(0);
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex(prev => (prev + 1) % ACTIVITY_FEED.length);
    }, 4000);
    return () => clearInterval(interval);
  }, []);
  const item = ACTIVITY_FEED[currentIndex];
  return <div className="h-8 overflow-hidden relative" data-id="element-1156">
      <AnimatePresence mode="wait" data-id="element-1157">
        <motion.div key={currentIndex} initial={{
        y: 20,
        opacity: 0
      }} animate={{
        y: 0,
        opacity: 1
      }} exit={{
        y: -20,
        opacity: 0
      }} transition={{
        duration: 0.3
      }} className="flex items-center justify-center gap-2 text-sm" data-id="element-1158">
          <span data-id="element-1159">{item.icon}</span>
          <span className="text-white font-medium" data-id="element-1160">{item.name}</span>
          <span className="text-[#B0B0B0]" data-id="element-1161">{item.action}</span>
          <span className="text-[#666666]" data-id="element-1162">— {item.time}</span>
        </motion.div>
      </AnimatePresence>
    </div>;
}
// Animated counter for social proof
function AnimatedSocialNumber({
  target,
  suffix = ''
}: {
  target: string;
  suffix?: string;
}) {
  return <motion.span initial={{
    opacity: 0,
    y: 8
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true
  }} transition={{
    duration: 0.5
  }} data-id="element-1163">
      {target}
      {suffix}
    </motion.span>;
}
export function HomePage() {
  const {
    isCustomerLoggedIn,
    currentCustomer
  } = useAuth();
  const countdown = useCountdown();
  const [showSticky, setShowSticky] = useState(false);
  const [showSpinWheel, setShowSpinWheel] = useState(false);
  // Show spin wheel popup after 3 minutes for non-logged-in users
  useEffect(() => {
    if (isCustomerLoggedIn) return;
    const dismissed = sessionStorage.getItem('mhp_spin_dismissed');
    if (dismissed) return;
    const timer = setTimeout(() => {
      setShowSpinWheel(true);
    }, 180000); // 3 minutes
    return () => clearTimeout(timer);
  }, [isCustomerLoggedIn]);
  const handleCloseSpinWheel = () => {
    setShowSpinWheel(false);
    sessionStorage.setItem('mhp_spin_dismissed', '1');
  };
  // Show sticky CTA after scrolling past hero
  useEffect(() => {
    function handleScroll() {
      setShowSticky(window.scrollY > 600);
    }
    window.addEventListener('scroll', handleScroll, {
      passive: true
    });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  const endOfMonthLabel = useMemo(() => {
    const now = new Date();
    const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    return `${end.getDate()}/${(end.getMonth() + 1).toString().padStart(2, '0')}`;
  }, []);
  return <div className="w-full" data-id="element-1164">
      {/* URGENCY COUNTDOWN BANNER */}
      <motion.div initial={{
      opacity: 0,
      y: -20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.5
    }} className="relative overflow-hidden" data-id="element-1165">
        <div className="absolute inset-0 bg-gradient-to-r from-[#EF4444]/10 via-[#C8A84E]/10 to-[#EF4444]/10" data-id="element-1166" />
        <motion.div className="absolute inset-0 bg-gradient-to-r from-transparent via-[#C8A84E]/5 to-transparent" animate={{
        x: ['-100%', '100%']
      }} transition={{
        duration: 3,
        repeat: Infinity,
        ease: 'linear'
      }} data-id="element-1167" />
        <div className="relative border-b border-[#C8A84E]/20 py-3 px-4" data-id="element-1168">
          <div className="max-w-5xl mx-auto flex flex-col md:flex-row items-center justify-center gap-3 md:gap-6" data-id="element-1169">
            <div className="flex items-center gap-2" data-id="element-1170">
              <motion.div animate={{
              scale: [1, 1.2, 1]
            }} transition={{
              duration: 1.5,
              repeat: Infinity
            }} data-id="element-1171">
                <FlameIcon className="w-5 h-5 text-[#EF4444]" data-id="element-1172" />
              </motion.div>
              <span className="text-sm font-semibold text-white" data-id="element-1173">
                Đăng ký trước {endOfMonthLabel}:
              </span>
              <span className="text-sm font-bold text-[#C8A84E]" data-id="element-1174">
                +200 điểm thưởng miễn phí!
              </span>
            </div>
            <div className="flex items-center gap-2" data-id="element-1175">
              <div className="flex items-center gap-1.5" data-id="element-1176">
                <CountdownUnit value={countdown.days} label="Ngày" data-id="element-1177" />
                <span className="text-[#666666] font-bold text-lg mb-4" data-id="element-1178">:</span>
                <CountdownUnit value={countdown.hours} label="Giờ" data-id="element-1179" />
                <span className="text-[#666666] font-bold text-lg mb-4" data-id="element-1180">:</span>
                <CountdownUnit value={countdown.minutes} label="Phút" data-id="element-1181" />
                <span className="text-[#666666] font-bold text-lg mb-4" data-id="element-1182">:</span>
                <CountdownUnit value={countdown.seconds} label="Giây" data-id="element-1183" />
              </div>
            </div>
            <div className="hidden md:flex items-center gap-1.5 text-xs" data-id="element-1184">
              <motion.div className="w-2 h-2 rounded-full bg-[#EF4444]" animate={{
              opacity: [1, 0.3, 1]
            }} transition={{
              duration: 1,
              repeat: Infinity
            }} data-id="element-1185" />
              <span className="text-[#EF4444] font-semibold" data-id="element-1186">
                Chỉ còn 23 suất
              </span>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Hero Section */}
      <section className="relative overflow-hidden" data-id="element-1187">
        <div className="absolute inset-0 bg-gradient-to-b from-[#C8A84E]/5 via-transparent to-transparent" data-id="element-1188" />
        <div className="absolute top-20 left-1/4 w-96 h-96 bg-[#C8A84E]/5 rounded-full blur-3xl" data-id="element-1189" />
        <div className="absolute bottom-0 right-1/4 w-72 h-72 bg-[#C8A84E]/3 rounded-full blur-3xl" data-id="element-1190" />

        <div className="relative max-w-7xl mx-auto px-4 pt-12 pb-16 md:pt-20 md:pb-24" data-id="element-1191">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.7
        }} className="text-center max-w-3xl mx-auto" data-id="element-1192">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-[#C8A84E]/10 border border-[#C8A84E]/20 rounded-full mb-6" data-id="element-1193">
              <SparklesIcon className="w-4 h-4 text-[#C8A84E]" data-id="element-1194" />
              <span className="text-sm font-medium text-[#C8A84E]" data-id="element-1195">
                Chương Trình Đối Tác B2B
              </span>
            </div>

            <h1 className="font-display text-4xl md:text-6xl lg:text-7xl font-bold text-white mb-4 leading-tight" data-id="element-1196">
              Giảm Chi Phí,{' '}
              <span className="text-[#C8A84E] italic" data-id="element-1197">Tăng Lợi Nhuận</span>
            </h1>
            <p className="text-lg md:text-xl text-[#B0B0B0] mb-6 max-w-2xl mx-auto leading-relaxed" data-id="element-1198">
              Đối tác B2B nhận ngay{' '}
              <span className="text-[#22C55E] font-semibold" data-id="element-1199">
                +2.5% tín dụng thưởng
              </span>{' '}
              mỗi lần nạp, tích điểm đổi thêm tín dụng mua hàng, và nâng hạng
              nhận ưu đãi độc quyền.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mb-4" data-id="element-1200">
              {isCustomerLoggedIn ? <Link to="/vi" data-id="element-1201">
                  <Button size="lg" data-id="element-1202">
                    <WalletIcon className="w-5 h-5" data-id="element-1203" />
                    Nạp Tiền Ngay
                  </Button>
                </Link> : <Link to="/dang-nhap" onClick={() => sessionStorage.setItem('mhp_signup_bonus', 'promo')} data-id="element-1204">
                  <Button size="lg" data-id="element-1205">
                    Đăng Ký Miễn Phí — Nhận 200 Điểm
                    <ArrowRightIcon className="w-5 h-5" data-id="element-1206" />
                  </Button>
                </Link>}
              <a href="#savings-calculator" data-id="element-1207">
                <Button variant="secondary" size="lg" data-id="element-1208">
                  Tính Lợi Ích Của Bạn
                </Button>
              </a>
            </div>

            {/* TRUST / ZERO-RISK BADGES */}
            <motion.div initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} transition={{
            delay: 0.5
          }} className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-xs text-[#B0B0B0]" data-id="element-1209">
              <span className="flex items-center gap-1.5" data-id="element-1210">
                <CheckIcon className="w-3.5 h-3.5 text-[#22C55E]" data-id="element-1211" />
                Miễn phí 100%
              </span>
              <span className="flex items-center gap-1.5" data-id="element-1212">
                <CheckIcon className="w-3.5 h-3.5 text-[#22C55E]" data-id="element-1213" />
                Không ràng buộc
              </span>
              <span className="flex items-center gap-1.5" data-id="element-1214">
                <CheckIcon className="w-3.5 h-3.5 text-[#22C55E]" data-id="element-1215" />
                Dùng tín dụng mua hàng bất cứ lúc nào
              </span>
              <span className="flex items-center gap-1.5" data-id="element-1216">
                <ShieldCheckIcon className="w-3.5 h-3.5 text-[#22C55E]" data-id="element-1217" />
                Bảo mật tuyệt đối
              </span>
            </motion.div>
          </motion.div>

          {/* SOCIAL PROOF STATS */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.3,
          duration: 0.6
        }} className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 max-w-4xl mx-auto" data-id="element-1218">
            <div className="text-center p-4 bg-[#1A1A1A]/60 border border-[#2A2A2A] rounded-xl" data-id="element-1219">
              <div className="flex items-center justify-center gap-1.5 mb-1" data-id="element-1220">
                <UsersIcon className="w-4 h-4 text-[#C8A84E]" data-id="element-1221" />
              </div>
              <p className="text-2xl md:text-3xl font-bold text-white" data-id="element-1222">
                <AnimatedSocialNumber target="1,247" suffix="+" data-id="element-1223" />
              </p>
              <p className="text-[10px] md:text-xs text-[#666666] mt-1" data-id="element-1224">
                Đối Tác Đã Tham Gia
              </p>
            </div>
            <div className="text-center p-4 bg-[#1A1A1A]/60 border border-[#2A2A2A] rounded-xl" data-id="element-1225">
              <div className="flex items-center justify-center gap-1.5 mb-1" data-id="element-1226">
                <TrendingUpIcon className="w-4 h-4 text-[#22C55E]" data-id="element-1227" />
              </div>
              <p className="text-2xl md:text-3xl font-bold text-[#22C55E]" data-id="element-1228">
                <AnimatedSocialNumber target="8.7" suffix=" tỷ" data-id="element-1229" />
              </p>
              <p className="text-[10px] md:text-xs text-[#666666] mt-1" data-id="element-1230">
                Đã Tiết Kiệm Cho Đối Tác
              </p>
            </div>
            <div className="text-center p-4 bg-[#1A1A1A]/60 border border-[#2A2A2A] rounded-xl" data-id="element-1231">
              <p className="text-2xl md:text-3xl font-bold text-[#C8A84E]" data-id="element-1232">
                +2.5%
              </p>
              <p className="text-[10px] md:text-xs text-[#666666] mt-1" data-id="element-1233">
                Thưởng Mỗi Lần Nạp
              </p>
            </div>
            <div className="text-center p-4 bg-[#1A1A1A]/60 border border-[#2A2A2A] rounded-xl" data-id="element-1234">
              <p className="text-2xl md:text-3xl font-bold text-white" data-id="element-1235">x2</p>
              <p className="text-[10px] md:text-xs text-[#666666] mt-1" data-id="element-1236">
                Điểm Thưởng VIP
              </p>
            </div>
          </motion.div>

          {/* LIVE ACTIVITY FEED */}
          <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          delay: 0.6
        }} className="mt-8 max-w-2xl mx-auto" data-id="element-1237">
            <div className="bg-[#1A1A1A]/40 border border-[#2A2A2A]/50 rounded-full py-2 px-4" data-id="element-1238">
              <LiveActivityFeed data-id="element-1239" />
            </div>
          </motion.div>
        </div>
      </section>

      {/* PREMIUM MEMBER CARD */}
      <section className="py-16 md:py-24 px-4" data-id="element-1240">
        <div className="max-w-3xl mx-auto" data-id="element-1241">
          <motion.div initial={{
          opacity: 0,
          y: 24
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-center mb-10" data-id="element-1242">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-[#C8A84E]/10 border border-[#C8A84E]/20 rounded-full mb-4" data-id="element-1243">
              <CrownIcon className="w-4 h-4 text-[#C8A84E]" data-id="element-1244" />
              <span className="text-sm font-medium text-[#C8A84E]" data-id="element-1245">
                Thẻ Thành Viên Kim Loại
              </span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-3" data-id="element-1246">
              Dấu Ấn{' '}
              <span className="text-[#C8A84E] italic font-display" data-id="element-1247">
                Đẳng Cấp
              </span>
            </h2>
            <p className="text-[#B0B0B0] max-w-lg mx-auto" data-id="element-1248">
              Thẻ kim loại cao cấp khắc tên riêng — biểu tượng đối tác MHP
            </p>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.15
        }} data-id="element-1249">
            <MemberCard3D defaultName={isCustomerLoggedIn ? currentCustomer?.name || '' : ''} customerId={isCustomerLoggedIn ? currentCustomer?.id : undefined} data-id="element-1250" />
          </motion.div>

          <motion.div initial={{
          opacity: 0
        }} whileInView={{
          opacity: 1
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.3
        }} className="text-center mt-4 space-y-2" data-id="element-1251">
            <p className="text-xs text-[#666666]" data-id="element-1252">
              Di chuột để xoay 3D · Nhấn nút bên dưới để lật thẻ
            </p>
            <p className="text-xs text-[#B0B0B0]" data-id="element-1253">
              Thẻ sẽ được gửi đến bạn sau khi đăng ký thành viên
            </p>
          </motion.div>
        </div>
      </section>

      {/* Savings Calculator */}
      <div className="bg-[#141414]" data-id="element-1254">
        <SavingsCalculator data-id="element-1255" />
      </div>

      {/* How It Works */}
      <section className="py-16 md:py-24 px-4" data-id="element-1256">
        <div className="max-w-7xl mx-auto" data-id="element-1257">
          <motion.div initial={{
          opacity: 0,
          y: 24
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-center mb-12" data-id="element-1258">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-3" data-id="element-1259">
              Cách Thức Hoạt Động
            </h2>
            <p className="text-[#B0B0B0] max-w-xl mx-auto" data-id="element-1260">
              3 bước đơn giản — bắt đầu tiết kiệm ngay hôm nay
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto" data-id="element-1261">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.1
          }} className="relative bg-[#1A1A1A] border border-[#2A2A2A] rounded-xl p-6 text-center" data-id="element-1262">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 w-7 h-7 bg-[#C8A84E] rounded-full flex items-center justify-center text-[#0D0D0D] text-sm font-bold" data-id="element-1263">
                1
              </div>
              <div className="w-14 h-14 bg-[#C8A84E]/10 rounded-xl flex items-center justify-center mx-auto mb-4 mt-2" data-id="element-1264">
                <WalletIcon className="w-7 h-7 text-[#C8A84E]" data-id="element-1265" />
              </div>
              <h3 className="text-lg font-semibold text-white mb-2" data-id="element-1266">
                Nạp Tiền Vào Ví
              </h3>
              <p className="text-sm text-[#B0B0B0]" data-id="element-1267">
                Nạp từ 10.000.000đ trở lên. Nhận ngay{' '}
                <span className="text-[#22C55E] font-semibold" data-id="element-1268">
                  +2.5% tín dụng thưởng
                </span>{' '}
                vào ví.
              </p>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.2
          }} className="relative bg-[#1A1A1A] border border-[#2A2A2A] rounded-xl p-6 text-center" data-id="element-1269">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 w-7 h-7 bg-[#C8A84E] rounded-full flex items-center justify-center text-[#0D0D0D] text-sm font-bold" data-id="element-1270">
                2
              </div>
              <div className="w-14 h-14 bg-[#C8A84E]/10 rounded-xl flex items-center justify-center mx-auto mb-4 mt-2" data-id="element-1271">
                <StarIcon className="w-7 h-7 text-[#C8A84E]" data-id="element-1272" />
              </div>
              <h3 className="text-lg font-semibold text-white mb-2" data-id="element-1273">
                Mua Hàng & Tích Điểm
              </h3>
              <p className="text-sm text-[#B0B0B0]" data-id="element-1274">
                Mỗi 100.000đ = 1 điểm. Hạng càng cao,{' '}
                <span className="text-[#22C55E] font-semibold" data-id="element-1275">
                  tích điểm càng nhanh
                </span>{' '}
                — lên đến x2.
              </p>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.3
          }} className="relative bg-[#1A1A1A] border border-[#2A2A2A] rounded-xl p-6 text-center" data-id="element-1276">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 w-7 h-7 bg-[#C8A84E] rounded-full flex items-center justify-center text-[#0D0D0D] text-sm font-bold" data-id="element-1277">
                3
              </div>
              <div className="w-14 h-14 bg-[#C8A84E]/10 rounded-xl flex items-center justify-center mx-auto mb-4 mt-2" data-id="element-1278">
                <GiftIcon className="w-7 h-7 text-[#C8A84E]" data-id="element-1279" />
              </div>
              <h3 className="text-lg font-semibold text-white mb-2" data-id="element-1280">
                Đổi Điểm Lấy Tín Dụng
              </h3>
              <p className="text-sm text-[#B0B0B0]" data-id="element-1281">
                Đổi điểm thành tín dụng mua hàng.{' '}
                <span className="text-[#22C55E] font-semibold" data-id="element-1282">
                  1.000 điểm = 500.000đ
                </span>{' '}
                tín dụng trong cửa hàng.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 md:py-24 px-4 bg-[#141414]" data-id="element-1283">
        <div className="max-w-7xl mx-auto" data-id="element-1284">
          <motion.div initial={{
          opacity: 0,
          y: 24
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-center mb-12" data-id="element-1285">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-3" data-id="element-1286">
              Quyền Lợi Đối Tác
            </h2>
            <p className="text-[#B0B0B0]" data-id="element-1287">
              Càng mua nhiều, càng nhận nhiều — thiết kế cho doanh nghiệp
            </p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-1288">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.05
          }} className="bg-[#1A1A1A] border border-[#2A2A2A] rounded-xl p-5" data-id="element-1289">
              <div className="flex items-center gap-3 mb-3" data-id="element-1290">
                <div className="w-10 h-10 bg-[#666666]/20 rounded-lg flex items-center justify-center" data-id="element-1291">
                  <span className="text-lg" data-id="element-1292">🏷️</span>
                </div>
                <div data-id="element-1293">
                  <h3 className="font-semibold text-white" data-id="element-1294">Thành Viên</h3>
                  <p className="text-xs text-[#666666]" data-id="element-1295">0 – 999 điểm</p>
                </div>
              </div>
              <ul className="space-y-2" data-id="element-1296">
                <li className="flex items-center gap-2 text-sm text-[#B0B0B0]" data-id="element-1297">
                  <CheckCircleIcon className="w-4 h-4 text-[#666666] flex-shrink-0" data-id="element-1298" />
                  Tích điểm x1
                </li>
                <li className="flex items-center gap-2 text-sm text-[#B0B0B0]" data-id="element-1299">
                  <CheckCircleIcon className="w-4 h-4 text-[#666666] flex-shrink-0" data-id="element-1300" />
                  Ví tín dụng +2.5% thưởng
                </li>
                <li className="flex items-center gap-2 text-sm text-[#B0B0B0]" data-id="element-1301">
                  <CheckCircleIcon className="w-4 h-4 text-[#666666] flex-shrink-0" data-id="element-1302" />
                  Đổi điểm → tín dụng mua hàng
                </li>
              </ul>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.1
          }} className="bg-[#1A1A1A] border border-[#A0AEC0]/20 rounded-xl p-5" data-id="element-1303">
              <div className="flex items-center gap-3 mb-3" data-id="element-1304">
                <div className="w-10 h-10 bg-[#A0AEC0]/15 rounded-lg flex items-center justify-center" data-id="element-1305">
                  <span className="text-lg" data-id="element-1306">🥈</span>
                </div>
                <div data-id="element-1307">
                  <h3 className="font-semibold text-[#A0AEC0]" data-id="element-1308">Bạc</h3>
                  <p className="text-xs text-[#666666]" data-id="element-1309">1.000 – 4.999 điểm</p>
                </div>
              </div>
              <ul className="space-y-2" data-id="element-1310">
                <li className="flex items-center gap-2 text-sm text-[#B0B0B0]" data-id="element-1311">
                  <CheckCircleIcon className="w-4 h-4 text-[#A0AEC0] flex-shrink-0" data-id="element-1312" />
                  Tích điểm x1.2
                </li>
                <li className="flex items-center gap-2 text-sm text-[#B0B0B0]" data-id="element-1313">
                  <CheckCircleIcon className="w-4 h-4 text-[#A0AEC0] flex-shrink-0" data-id="element-1314" />
                  Đổi điểm → tín dụng mua hàng
                </li>
                <li className="flex items-center gap-2 text-sm text-[#B0B0B0]" data-id="element-1315">
                  <CheckCircleIcon className="w-4 h-4 text-[#A0AEC0] flex-shrink-0" data-id="element-1316" />
                  Quà sinh nhật
                </li>
              </ul>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.15
          }} className="bg-[#1A1A1A] border border-[#C8A84E]/30 rounded-xl p-5 ring-1 ring-[#C8A84E]/10" data-id="element-1317">
              <div className="flex items-center gap-3 mb-3" data-id="element-1318">
                <div className="w-10 h-10 bg-[#C8A84E]/15 rounded-lg flex items-center justify-center" data-id="element-1319">
                  <span className="text-lg" data-id="element-1320">🥇</span>
                </div>
                <div data-id="element-1321">
                  <h3 className="font-semibold text-[#C8A84E]" data-id="element-1322">Vàng</h3>
                  <p className="text-xs text-[#666666]" data-id="element-1323">5.000 – 14.999 điểm</p>
                </div>
              </div>
              <ul className="space-y-2" data-id="element-1324">
                <li className="flex items-center gap-2 text-sm text-[#B0B0B0]" data-id="element-1325">
                  <CheckCircleIcon className="w-4 h-4 text-[#C8A84E] flex-shrink-0" data-id="element-1326" />
                  Tích điểm x1.5
                </li>
                <li className="flex items-center gap-2 text-sm text-[#B0B0B0]" data-id="element-1327">
                  <CheckCircleIcon className="w-4 h-4 text-[#C8A84E] flex-shrink-0" data-id="element-1328" />
                  Đổi điểm → tín dụng mua hàng
                </li>
                <li className="flex items-center gap-2 text-sm text-[#B0B0B0]" data-id="element-1329">
                  <CheckCircleIcon className="w-4 h-4 text-[#C8A84E] flex-shrink-0" data-id="element-1330" />
                  Quà sinh nhật + Ưu đãi riêng
                </li>
              </ul>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.2
          }} className="bg-gradient-to-br from-[#1A1A1A] to-[#EF4444]/5 border border-[#EF4444]/20 rounded-xl p-5" data-id="element-1331">
              <div className="flex items-center gap-3 mb-3" data-id="element-1332">
                <div className="w-10 h-10 bg-[#EF4444]/15 rounded-lg flex items-center justify-center" data-id="element-1333">
                  <span className="text-lg" data-id="element-1334">👑</span>
                </div>
                <div data-id="element-1335">
                  <h3 className="font-semibold text-[#EF4444]" data-id="element-1336">VIP</h3>
                  <p className="text-xs text-[#666666]" data-id="element-1337">15.000+ điểm</p>
                </div>
              </div>
              <ul className="space-y-2" data-id="element-1338">
                <li className="flex items-center gap-2 text-sm text-[#B0B0B0]" data-id="element-1339">
                  <CheckCircleIcon className="w-4 h-4 text-[#EF4444] flex-shrink-0" data-id="element-1340" />
                  Tích điểm x2
                </li>
                <li className="flex items-center gap-2 text-sm text-[#B0B0B0]" data-id="element-1341">
                  <CheckCircleIcon className="w-4 h-4 text-[#EF4444] flex-shrink-0" data-id="element-1342" />
                  Đổi điểm → tín dụng mua hàng
                </li>
                <li className="flex items-center gap-2 text-sm text-[#B0B0B0]" data-id="element-1343">
                  <CheckCircleIcon className="w-4 h-4 text-[#EF4444] flex-shrink-0" data-id="element-1344" />
                  Tất cả quyền lợi cao cấp
                </li>
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* TESTIMONIAL + SOCIAL PROOF */}
      <section className="py-16 px-4" data-id="element-1345">
        <div className="max-w-3xl mx-auto" data-id="element-1346">
          <motion.div initial={{
          opacity: 0,
          y: 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="bg-[#1A1A1A] border border-[#2A2A2A] rounded-2xl p-6 md:p-8" data-id="element-1347">
            <div className="flex items-start gap-4" data-id="element-1348">
              <div className="w-12 h-12 bg-[#C8A84E]/15 rounded-full flex items-center justify-center flex-shrink-0 text-xl" data-id="element-1349">
                👤
              </div>
              <div data-id="element-1350">
                <div className="flex items-center gap-1 mb-2" data-id="element-1351">
                  {[1, 2, 3, 4, 5].map(s => <StarIcon key={s} className="w-4 h-4 text-[#C8A84E] fill-[#C8A84E]" data-id="element-1352" />)}
                </div>
                <blockquote className="text-[#B0B0B0] text-sm md:text-base leading-relaxed mb-3 italic" data-id="element-1353">
                  "Trước đây mua phụ tùng ở đâu cũng giá như nhau. Từ khi tham
                  gia chương trình đối tác MHP, mỗi tháng tiết kiệm được 3-4
                  triệu chỉ từ tín dụng thưởng. Tích điểm đổi thêm tín dụng nữa.
                  Đơn giản mà hiệu quả."
                </blockquote>
                <div data-id="element-1354">
                  <p className="text-sm font-semibold text-white" data-id="element-1355">
                    Anh Trần Minh Tuấn
                  </p>
                  <p className="text-xs text-[#666666]" data-id="element-1356">
                    Chủ garage ô tô — Đối tác Vàng, TP.HCM
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ENHANCED CTA */}
      <section className="py-16 px-4 bg-[#141414]" data-id="element-1357">
        <div className="max-w-3xl mx-auto text-center" data-id="element-1358">
          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} className="relative bg-gradient-to-br from-[#C8A84E]/10 to-[#C8A84E]/5 border border-[#C8A84E]/20 rounded-2xl p-8 md:p-12 overflow-hidden" data-id="element-1359">
            {/* Urgency pulse */}
            <motion.div className="absolute inset-0 border-2 border-[#C8A84E]/20 rounded-2xl" animate={{
            opacity: [0.2, 0.5, 0.2]
          }} transition={{
            duration: 2,
            repeat: Infinity
          }} data-id="element-1360" />

            <CrownIcon className="w-12 h-12 text-[#C8A84E] mx-auto mb-4" data-id="element-1361" />
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-3" data-id="element-1362">
              Mỗi Ngày Chậm Trễ = Mất Tiền Thật
            </h2>
            <p className="text-[#B0B0B0] mb-4 max-w-lg mx-auto" data-id="element-1363">
              Tham gia miễn phí. Nạp tiền lần đầu và nhận ngay 3% thưởng. Không
              ràng buộc, không rủi ro.
            </p>

            {/* Mini countdown */}
            <div className="flex items-center justify-center gap-2 mb-6" data-id="element-1364">
              <ClockIcon className="w-4 h-4 text-[#EF4444]" data-id="element-1365" />
              <span className="text-sm text-[#EF4444] font-semibold" data-id="element-1366">
                Còn {countdown.days} ngày {countdown.hours} giờ để nhận 200 điểm
                miễn phí
              </span>
            </div>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mb-4" data-id="element-1367">
              <Link to={isCustomerLoggedIn ? '/vi' : '/dang-nhap'} onClick={() => {
              if (!isCustomerLoggedIn) sessionStorage.setItem('mhp_signup_bonus', 'promo');
            }} data-id="element-1368">
                <Button size="lg" data-id="element-1369">
                  {isCustomerLoggedIn ? 'Nạp Tiền Ngay' : 'Đăng Ký Miễn Phí'}
                  <ArrowRightIcon className="w-5 h-5" data-id="element-1370" />
                </Button>
              </Link>
            </div>

            <div className="flex items-center justify-center gap-4 text-xs text-[#B0B0B0]" data-id="element-1371">
              <span className="flex items-center gap-1" data-id="element-1372">
                <CheckIcon className="w-3 h-3 text-[#22C55E]" data-id="element-1373" />
                Miễn phí
              </span>
              <span className="flex items-center gap-1" data-id="element-1374">
                <CheckIcon className="w-3 h-3 text-[#22C55E]" data-id="element-1375" />
                30 giây đăng ký
              </span>
              <span className="flex items-center gap-1" data-id="element-1376">
                <CheckIcon className="w-3 h-3 text-[#22C55E]" data-id="element-1377" />
                Nhận thưởng ngay
              </span>
            </div>

            {/* Scarcity */}
            <div className="mt-4 flex items-center justify-center gap-1.5 text-xs" data-id="element-1378">
              <motion.div className="w-2 h-2 rounded-full bg-[#EF4444]" animate={{
              opacity: [1, 0.3, 1]
            }} transition={{
              duration: 1,
              repeat: Infinity
            }} data-id="element-1379" />
              <span className="text-[#EF4444]" data-id="element-1380">
                Chỉ còn 23 suất nhận thưởng đăng ký tháng này
              </span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* STICKY MOBILE CTA */}
      <AnimatePresence data-id="element-1381">
        {showSticky && !isCustomerLoggedIn && <motion.div initial={{
        y: 100
      }} animate={{
        y: 0
      }} exit={{
        y: 100
      }} transition={{
        type: 'spring',
        stiffness: 300,
        damping: 30
      }} className="fixed bottom-0 left-0 right-0 z-50 md:hidden bg-[#0D0D0D]/95 backdrop-blur-lg border-t border-[#2A2A2A] px-4 py-3 safe-area-pb" data-id="element-1382">
            <div className="flex items-center gap-3" data-id="element-1383">
              <div className="flex-1 min-w-0" data-id="element-1384">
                <p className="text-xs text-[#B0B0B0] truncate" data-id="element-1385">
                  <FlameIcon className="w-3 h-3 text-[#EF4444] inline mr-1" data-id="element-1386" />
                  Còn {countdown.days}d {countdown.hours}h — 23 suất
                </p>
              </div>
              <Link to="/dang-nhap" className="flex-shrink-0" data-id="element-1387">
                <Button size="sm" data-id="element-1388">
                  Đăng Ký Miễn Phí
                  <ArrowRightIcon className="w-4 h-4" data-id="element-1389" />
                </Button>
              </Link>
            </div>
          </motion.div>}
      </AnimatePresence>

      {/* Spin Wheel Popup */}
      <SpinWheelPopup isOpen={showSpinWheel} onClose={handleCloseSpinWheel} isLoggedIn={isCustomerLoggedIn} data-id="element-1390" />
    </div>;
}